const EMPTY_SLOT = Object.freeze({
    id: '',
    amount: '',
    level: ''
})

// Live GGE presets arrive in front-left-right order for tools first,
// then front-left-right order for troops.
const PRESET_ROW_INDEX = Object.freeze({
    middleTools: 0,
    leftTools: 1,
    rightTools: 2,
    middleTroops: 3,
    leftTroops: 4,
    rightTroops: 5
})

const normalizePresetValue = (value, fallback = -1) => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? Math.floor(numericValue) : fallback
}

const normalizePresetAmount = value =>
    Math.max(0, normalizePresetValue(value, 0))

const looksLikePresetEntryMetadata = value => {
    const numericValue = normalizePresetValue(value)
    return numericValue === 0
}

const looksLikePresetUnitID = value => {
    const numericValue = normalizePresetValue(value)
    return numericValue > 0
}

const normalizePresetPair = (unitValue, amountValue) => {
    const unitId = normalizePresetValue(unitValue)
    const amount = normalizePresetAmount(amountValue)

    if (unitId <= 0 || amount <= 0)
        return undefined

    return [unitId, amount]
}

const isMetadataPrefixedFlatRow = row =>
    row.length >= 3 &&
    row.length % 3 === 0 &&
    row.every((_, index) => index % 3 !== 0 || looksLikePresetEntryMetadata(row[index]))

const normalizePresetEntry = entry => {
    if (!Array.isArray(entry))
        return undefined

    if (entry.length >= 2 && Array.isArray(entry[1]))
        return normalizePresetPair(entry[1]?.[0], entry[1]?.[1])

    if (
        entry.length >= 3 &&
        looksLikePresetEntryMetadata(entry[0]) &&
        looksLikePresetUnitID(entry[1])
    ) {
        return normalizePresetPair(entry[1], entry[2])
    }

    if (entry.length >= 3 && typeof entry[0] === 'string') {
        return normalizePresetPair(entry[1], entry[2])
    }

    if (entry.length >= 2) {
        return normalizePresetPair(entry[0], entry[1])
    }

    return undefined
}

export const normalizeGamePresetRow = row => {
    if (!Array.isArray(row))
        return []

    if (row.every(entry => Array.isArray(entry) && entry.length >= 2)) {
        return row.map(normalizePresetEntry).filter(Boolean)
    }

    if (isMetadataPrefixedFlatRow(row)) {
        const normalizedRow = []
        for (let index = 0; index + 2 < row.length; index += 3) {
            const normalizedEntry = normalizePresetPair(row[index + 1], row[index + 2])
            if (normalizedEntry)
                normalizedRow.push(normalizedEntry)
        }

        return normalizedRow
    }

    const normalizedRow = []
    for (let index = 0; index + 1 < row.length; index += 2) {
        const normalizedEntry = normalizePresetPair(row[index], row[index + 1])
        if (normalizedEntry)
            normalizedRow.push(normalizedEntry)
    }

    return normalizedRow
}

export const normalizeGamePresetUnits = units =>
    Array.isArray(units) ? units.slice(0, 6).map(normalizeGamePresetRow) : []

const createTemplateSlotFromPresetEntry = entry => {
    const unitId = normalizePresetValue(entry?.[0])
    const amount = normalizePresetAmount(entry?.[1])

    if (unitId <= 0 || amount <= 0)
        return { ...EMPTY_SLOT }

    return {
        id: String(unitId),
        amount: String(amount),
        level: ''
    }
}

const buildTemplateSlotsFromPresetRow = (row, slotCount) => {
    const normalizedRow = normalizeGamePresetRow(row)
    return Array.from({ length: slotCount }, (_, index) =>
        createTemplateSlotFromPresetEntry(normalizedRow[index]))
}

export const applyGamePresetToTemplateDraft = (template, targetWaveId, preset) => {
    const waves = Array.isArray(template?.waves) ? template.waves : []
    if (waves.length <= 0)
        return template

    const resolvedWaveId =
        waves.some(wave => wave?.id === targetWaveId) ?
            targetWaveId :
            (template?.defaultWaveId && waves.some(wave => wave?.id === template.defaultWaveId) ?
                template.defaultWaveId :
                waves[0]?.id)

    if (!resolvedWaveId)
        return template

    const presetRows = normalizeGamePresetUnits(preset?.units ?? preset)

    return {
        ...template,
        waves: waves.map(wave => {
            if (wave?.id !== resolvedWaveId)
                return wave

            return {
                ...wave,
                left: {
                    ...wave.left,
                    tools: buildTemplateSlotsFromPresetRow(presetRows[PRESET_ROW_INDEX.leftTools], 2),
                    troops: buildTemplateSlotsFromPresetRow(presetRows[PRESET_ROW_INDEX.leftTroops], 2)
                },
                middle: {
                    ...wave.middle,
                    tools: buildTemplateSlotsFromPresetRow(presetRows[PRESET_ROW_INDEX.middleTools], 3),
                    troops: buildTemplateSlotsFromPresetRow(presetRows[PRESET_ROW_INDEX.middleTroops], 6)
                },
                right: {
                    ...wave.right,
                    tools: buildTemplateSlotsFromPresetRow(presetRows[PRESET_ROW_INDEX.rightTools], 2),
                    troops: buildTemplateSlotsFromPresetRow(presetRows[PRESET_ROW_INDEX.rightTroops], 2)
                }
            }
        })
    }
}
